import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import DataTable from '../../components/DataTable/DataTable';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import { BusinessPartnerTypeEnum } from '../../enums/BusinessPartnerTypeEnum';
import ProjectModel from '../../models/ProjectModel';
import DataService from '../../services/data.service';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import Form from './../../components/Fields/Form';
import MultiSelectField from './../../components/Fields/MultiSelectField';
import SelectListField from './../../components/Fields/SelectListField';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';
import Project_popup_CreateProject from './Popups/Project_popup_CreateProject';

interface ProjectSearchCriteria {
    quickSearch: string
    projectTypeIds: number[]
    projectStatusIds: number[]
    businessPartnerId?: number
    brandId?: number
    userId?: number
    active: boolean
    rememberFilter: boolean
    pageNumber: number
    pageSize: number
    sortBy: string
}

const ProjectSearchView: React.FC = () => {
    const [formData, setFormData] = useState<ProjectSearchCriteria>({
        quickSearch: '',
        projectTypeIds: [],
        projectStatusIds: [],
        active: true,
        rememberFilter: false,
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'CreatedOn'
    });

    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes?activeOnly=' + formData.active + '&applicationFunction=' + ApplicationFunctions.SearchProject);
    const projectStatuses = useSelectListOptions('api/v1/selectlist/ProjectStatuses?activeOnly=' + formData.active);
    const customers = useSelectListOptions('api/v1/selectlist/BusinessPartners?activeOnly=' + formData.active + '&businessPartnerTypeId=' + BusinessPartnerTypeEnum.Customer, '-- All customers');
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees?activeOnly=' + formData.active, '-- All brands');
    const users = useSelectListOptions('api/v1/selectlist/Users?activeOnly=' + formData.active, '-- All users');

    const [projects, setProjects] = useState<ProjectModel[]>([])
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();

    useEffect(() => {
        const filterItem = localStorage.getItem("projectFilter");
        if (filterItem !== null) {
            let filter: ProjectSearchCriteria = JSON.parse(filterItem);
            setFormData(x => ({ ...filter }));
            searchWithCriteria(filter)
        }
        else
            search()
    }, [])

    function search() {
        searchWithCriteria(formData)
    }


    function searchWithCriteria(searchCriteria) {
        setShowSpinner(true);

        if (searchCriteria.rememberFilter)
            localStorage.setItem('projectFilter', JSON.stringify(searchCriteria));
        else
            localStorage.removeItem(`projectFilter`);

        DataService
            .post<ProjectModel[]>(`api/v1/Projects/Search/`, searchCriteria)
            .then(response => {
                setShowSpinner(false);
                setProjects(response);
                //console.log('Projects', response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            quickSearch: '',
            projectTypeIds: [],
            projectStatusIds: [],
            active: true,
            rememberFilter: formData?.rememberFilter ?? false,
            pageNumber: 1,
            pageSize: 100,
            sortBy: 'CreatedOn'
        }

        setFormData({ ...defaultCriteria })
    }

/*
    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }
*/

    const formConfig: FormConfig = {
        fields: [
            { name: 'quickSearch', label: 'Search', labelSize: 2 },
            { name: 'projectTypeIds', label: 'Project types', labelSize: 2 },
            { name: 'projectStatusIds', label: 'Project statuses', labelSize: 2 },
            { name: 'businessPartnerId', label: 'Customer', labelSize: 2 },
            { name: 'brandId', label: 'Brand', labelSize: 2 },
            { name: 'userId', label: 'User', labelSize: 2 },
            { name: 'active', label: 'Active projects only', labelSize: 2 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 2 },
        ],
        handleSubmit: search,
        //handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedForProjectType } = useAuth();
    if (!isAuthorizedForProjectType(ApplicationFunctions.SearchProject)) {
        return (<Unauthorized />);
    }

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new Project" onClick={() => { setShowCreatePopup(true); }} appFunction={ApplicationFunctions.CreateProject} projectTypeId={0} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Projects' actions={formActions} footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <TextboxField {...getFieldProps('quickSearch')} />
                    <MultiSelectField {...getFieldProps('projectTypeIds')} options={projectTypes} />
                    <MultiSelectField {...getFieldProps('projectStatusIds')} options={projectStatuses} />
                    <SelectListField {...getFieldProps('businessPartnerId')} options={customers} />
                    <SelectListField {...getFieldProps('brandId')} options={brands} />
                    <SelectListField {...getFieldProps('userId')} options={users} />
                    <SwitchField {...getFieldProps('active')} />
                    <SwitchField {...getFieldProps('rememberFilter')} />
                </Container>
            </Form>
            <div className="card" style={{margin: 12}}>
                <DataTable
                    name='dtSearchProject'
                    tableData={projects}
                    navigateTitle='Project'
                    navigateUrl='/plm/Projects/Details/'
                    colInfos={[
                        { dataProp: 'thumbnailUrl', dataType: 'bigimage', width: 75 },
                        { title: 'Nr', dataProp: 'id', dataType: 'number', sort: 'desc', key: true },
                        { title: 'Name', dataProp: 'name', filterInfo: { filterType: 'Textbox' } },
                        { title: 'Project type', dataProp: 'projectType', filterInfo: { filterType: 'CheckboxList' } },
                        { title: 'Status', dataProp: 'projectStatus', filterInfo: { filterType: 'CheckboxList' } },
                        { title: 'Business-Partners', dataProp: 'businessPartners', filterInfo: { filterType: 'Textbox' } },
                        { title: 'Users', dataProp: 'users', filterInfo: { filterType: 'Textbox' } },
                        { title: 'Started on', dataProp: 'startedOn', dataType: 'date' },
                        { title: 'Deadline', dataProp: 'deadline', dataType: 'date' },
                        { title: 'Finished on', dataProp: 'finishedOn', dataType: 'date' },
                    ]}
                    />
            </div>
            {showCreatePopup && <Project_popup_CreateProject show={showCreatePopup} onHide={() => setShowCreatePopup(false)} />}
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default ProjectSearchView;